import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  TrendingUp,
  Filter,
  Clock,
  BookOpen,
  Shuffle,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import WorkspacePill from '../../components/layout/WorkspacePill';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  semanticsApi,
  type SemanticsItem,
  type SemanticEntityType,
  type SemanticEntityView,
} from '../../services/semantics-api';
import { handleApiError } from '../../services/api';
import { useConnectTargetOrg } from '../../hooks/useConnectTargetOrg';
import DefinitionView from './DefinitionView';
import EditView from './EditView';
import HistoryView from './HistoryView';
import DiscussView from './DiscussView';

type SemanticsViewTab = 'read' | 'edit' | 'history' | 'discuss';

const TYPE_META: Record<
  SemanticEntityType,
  { label: string; singular: string; icon: React.ComponentType<{ className?: string }>; tile: string }
> = {
  metric: { label: 'Metrics', singular: 'Metric', icon: TrendingUp, tile: 'bg-success/15 text-success' },
  segment: { label: 'Segments', singular: 'Segment', icon: Filter, tile: 'bg-info/15 text-info' },
  time_window: { label: 'Time windows', singular: 'Time window', icon: Clock, tile: 'bg-warn/15 text-warn' },
  glossary: { label: 'Glossary', singular: 'Glossary term', icon: BookOpen, tile: 'bg-primary/15 text-primary' },
  product_mapping: { label: 'Mappings', singular: 'Product mapping', icon: Shuffle, tile: 'bg-muted text-muted-foreground' },
};
const TYPE_ORDER: SemanticEntityType[] = ['metric', 'segment', 'time_window', 'glossary', 'product_mapping'];

const SemanticsPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceFilter = searchParams.get('source');
  const view = (searchParams.get('view') as SemanticsViewTab | null) ?? 'read';
  const newMode = searchParams.get('new') === '1' && !entityId;
  const [reviewOnly, setReviewOnly] = useState(false);
  const queryClient = useQueryClient();

  // Which business a new definition belongs to — chosen explicitly, never the
  // active/last business. Single-business workspaces skip the picker.
  const { orgs, targetOrgId } = useConnectTargetOrg();
  const orgParam = searchParams.get('org');
  const chosenOrgId = orgParam && orgs.some((o) => o.id === orgParam) ? orgParam : targetOrgId;
  const chosenOrgName = orgs.find((o) => o.id === chosenOrgId)?.name ?? null;

  // Browsing spans the whole workspace; this narrows it (§8, page-local).
  const [orgFilter, setOrgFilter] = useState<string | null>(null);

  const { data: items = [], isLoading: treeLoading } = useQuery({
    queryKey: ['semanticsOverview'],
    queryFn: () => semanticsApi.getOverview(),
  });

  const { data: entity, isLoading: entityLoading, isError } = useQuery({
    queryKey: ['semanticEntity', entityId],
    queryFn: () => semanticsApi.getEntity(entityId!),
    enabled: !!entityId,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['semanticsOverview'] });
    void queryClient.invalidateQueries({ queryKey: ['semanticEntity', entityId] });
    void queryClient.invalidateQueries({ queryKey: ['semanticVersions', entityId] });
    void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
  };
  const confirm = useMutation({
    mutationFn: (versionId: string) => semanticsApi.confirmVersion(versionId),
    onSuccess: () => {
      invalidate();
      toast.success('Definition confirmed — the AI uses it from the next query');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Confirm failed'),
  });
  const discard = useMutation({
    mutationFn: (versionId: string) => semanticsApi.discardVersion(versionId),
    onSuccess: () => {
      invalidate();
      toast.success('Proposal discarded');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Discard failed'),
  });

  const bySource = useMemo(
    () =>
      items.filter(
        (i) =>
          (!sourceFilter || i.sourceId === sourceFilter) &&
          (!orgFilter || i.organizationId === orgFilter),
      ),
    [items, sourceFilter, orgFilter],
  );
  const proposedCount = bySource.filter((i) => i.proposedVersionId).length;
  const visible = reviewOnly ? bySource.filter((i) => i.proposedVersionId) : bySource;
  const multiOrg = useMemo(() => new Set(items.map((i) => i.organizationId)).size > 1, [items]);

  // No definition selected → jump to the first one (unless drafting a new one).
  useEffect(() => {
    if (entityId || newMode || visible.length === 0) return;
    navigate(`/semantics/${visible[0].id}`, { replace: true });
  }, [entityId, newMode, visible, navigate]);

  const startNew = (): void => {
    const next = new URLSearchParams();
    if (sourceFilter) next.set('source', sourceFilter);
    next.set('new', '1');
    navigate(`/semantics?${next.toString()}`);
  };

  const setView = (v: SemanticsViewTab): void => {
    const next = new URLSearchParams(searchParams);
    if (v === 'read') next.delete('view');
    else next.set('view', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="max-w-[82rem] mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Semantics</h1>
        <div className="flex items-center gap-2.5">
          <BusinessFilter value={orgFilter} onChange={setOrgFilter} />
          <WorkspacePill />
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New definition
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[252px_minmax(0,1fr)] gap-3.5 mt-4 items-start">
        <Tree
          items={visible}
          loading={treeLoading}
          activeId={entityId}
          multiOrg={multiOrg}
          proposedCount={proposedCount}
          reviewOnly={reviewOnly}
          onToggleReview={() => setReviewOnly((v) => !v)}
        />

        <div className="rounded-[10px] border border-border bg-card shadow-sm overflow-hidden min-h-[420px]">
          {newMode ? (
            <>
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border">
                <span className="w-[30px] h-[30px] rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-none">
                  <Plus className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-foreground">New definition</div>
                  <div className="text-[10.5px] text-muted-foreground mt-px">
                    {chosenOrgName
                      ? `For ${chosenOrgName} — describe it and the AI drafts a proposal you confirm`
                      : 'Choose which business this definition is for'}
                  </div>
                </div>
              </div>
              {chosenOrgId ? (
                <DiscussView
                  entity={null}
                  accountId={null}
                  organizationId={chosenOrgId}
                  onConfirm={() => undefined}
                  onDiscard={() => undefined}
                  busy={false}
                  onEntityCreated={(id) => navigate(`/semantics/${id}?view=discuss`)}
                />
              ) : (
                <OrgPickerStep
                  orgs={orgs}
                  defaultOrgId={targetOrgId}
                  onContinue={(id) => navigate(`/semantics?new=1&org=${id}`)}
                />
              )}
            </>
          ) : entityLoading || (!entity && !isError && entityId) ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <LoadingSpinner />
            </div>
          ) : !entityId ? (
            <div className="p-10 text-center text-[13px] text-muted-foreground">
              {items.length === 0
                ? 'No definitions yet — they appear here as the AI learns your business.'
                : 'Select a definition to view it.'}
            </div>
          ) : isError || !entity ? (
            <div className="p-10 text-center text-[13px] text-muted-foreground">
              This definition doesn&apos;t exist or you don&apos;t have access to it.
            </div>
          ) : (
            <>
              <DefHead entity={entity} multiOrg={multiOrg} />
              <ViewTabs entity={entity} active={view} onChange={setView} />
              {(view === 'read' || ((view === 'discuss' || view === 'edit') && !entity.canManage)) && (
                <DefinitionView
                  entity={entity}
                  onConfirm={() => entity.proposedVersionId && confirm.mutate(entity.proposedVersionId)}
                  onDiscard={() => entity.proposedVersionId && discard.mutate(entity.proposedVersionId)}
                  busy={confirm.isPending || discard.isPending}
                />
              )}
              {view === 'edit' && entity.canManage && (
                <EditView entity={entity} onDone={() => setView('read')} />
              )}
              {view === 'history' && <HistoryView entity={entity} />}
              {view === 'discuss' && entity.canManage && (
                <DiscussView
                  entity={entity}
                  accountId={entity.dataSourceAccountId}
                  onConfirm={() => entity.proposedVersionId && confirm.mutate(entity.proposedVersionId)}
                  onDiscard={() => entity.proposedVersionId && discard.mutate(entity.proposedVersionId)}
                  busy={confirm.isPending || discard.isPending}
                  onEntityCreated={() => undefined}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Doc header ──────────────────────────────────────────────────────────

const DefHead: React.FC<{ entity: SemanticEntityView; multiOrg: boolean }> = ({ entity, multiOrg }) => {
  const meta = TYPE_META[entity.type];
  return (
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border">
      <span className={clsx('w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-none', meta.tile)}>
        <meta.icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[15px] font-semibold text-foreground truncate">{entity.name}</div>
        <div className="text-[10.5px] text-muted-foreground mt-px">
          {meta.singular}
          {multiOrg && <> · {entity.orgName}</>}
          {entity.sourceName && <> · {entity.sourceName}</>}
          {entity.confirmedVersionNumber != null && <> · v{entity.confirmedVersionNumber}</>}
        </div>
      </div>
    </div>
  );
};

const ViewTabs: React.FC<{
  entity: SemanticEntityView;
  active: SemanticsViewTab;
  onChange: (v: SemanticsViewTab) => void;
}> = ({ entity, active, onChange }) => {
  const tab = (v: SemanticsViewTab, label: React.ReactNode): React.ReactNode => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={clsx(
        'px-3 py-2.5 text-[13px] font-medium -mb-px border-b-2 inline-flex items-center gap-1.5',
        active === v
          ? 'text-primary border-primary font-semibold bg-card'
          : 'text-muted-foreground border-transparent hover:text-foreground',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex gap-0.5 px-5 border-b border-border bg-muted/40">
      {tab('read', 'Read')}
      {entity.canManage && tab('edit', 'Edit')}
      {tab(
        'history',
        <>
          History
          {entity.proposedVersionId && (
            <span className="text-[9.5px] font-bold bg-warn/15 text-warn rounded-full px-1.5 py-px">
              1 pending
            </span>
          )}
        </>,
      )}
      {entity.canManage && tab('discuss', <>✦ Discuss with AI</>)}
    </div>
  );
};

// ── Tree ────────────────────────────────────────────────────────────────

const Tree: React.FC<{
  items: SemanticsItem[];
  loading: boolean;
  activeId: string | undefined;
  multiOrg: boolean;
  proposedCount: number;
  reviewOnly: boolean;
  onToggleReview: () => void;
}> = ({ items, loading, activeId, multiOrg, proposedCount, reviewOnly, onToggleReview }) => (
  <div className="rounded-[10px] border border-border bg-card shadow-sm py-2 text-[12.5px] lg:sticky lg:top-5">
    {loading ? (
      <div className="flex items-center justify-center py-10">
        <LoadingSpinner />
      </div>
    ) : (
      <>
        {proposedCount > 0 && (
          <button
            type="button"
            onClick={onToggleReview}
            className={clsx(
              'flex items-center gap-2 w-full px-3.5 py-2 mb-1 text-left',
              reviewOnly ? 'bg-warn/15 text-warn' : 'text-foreground hover:bg-muted',
            )}
          >
            <span className="w-5 h-5 rounded-md bg-warn/15 text-warn flex items-center justify-center flex-none">
              <AlertTriangle className="w-3 h-3" />
            </span>
            <span className="font-semibold">Review</span>
            <span className="ml-auto text-[10px] font-extrabold rounded-full bg-warn/20 text-warn px-1.5 py-px flex-none">
              {proposedCount}
            </span>
          </button>
        )}

        {items.length === 0 ? (
          <div className="px-3.5 py-8 text-center text-[12px] text-muted-foreground">
            {reviewOnly ? 'Nothing awaiting review.' : 'No definitions.'}
          </div>
        ) : (
          TYPE_ORDER.map((type) => {
            const ofType = items.filter((i) => i.type === type);
            if (ofType.length === 0) return null;
            const meta = TYPE_META[type];
            return (
              <div key={type}>
                <div className="flex items-center gap-2 px-3.5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <span className={clsx('w-[18px] h-[18px] rounded-[5px] flex items-center justify-center', meta.tile)}>
                    <meta.icon className="w-2.5 h-2.5" />
                  </span>
                  {meta.label} · {ofType.length}
                </div>
                {ofType.map((item) => (
                  <TreeItem key={item.id} item={item} active={activeId === item.id} multiOrg={multiOrg} />
                ))}
              </div>
            );
          })
        )}
      </>
    )}
  </div>
);

const TreeItem: React.FC<{ item: SemanticsItem; active: boolean; multiOrg: boolean }> = ({
  item,
  active,
  multiOrg,
}) => {
  const pending = !!item.proposedVersionId;
  const subtitle = [multiOrg ? item.orgName : null, item.sourceName]
    .filter(Boolean)
    .join(' · ');
  return (
    <Link
      to={`/semantics/${item.id}`}
      className={clsx(
        'flex items-center gap-2 py-1.5 pr-3 pl-[18px] min-w-0',
        active ? 'bg-accent text-primary font-semibold' : 'text-foreground hover:bg-muted',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{item.name}</span>
        {subtitle && <span className="block truncate text-[10px] text-muted-foreground font-normal">{subtitle}</span>}
      </span>
      {pending ? (
        <span className="ml-auto text-[8.5px] font-extrabold text-warn flex-none">PENDING</span>
      ) : (
        <span className="ml-auto w-[13px] h-[13px] rounded-full bg-success/15 text-success flex items-center justify-center text-[8px] font-extrabold flex-none">
          ✓
        </span>
      )}
    </Link>
  );
};

// ── New-definition org picker ─────────────────────────────────────────────

const OrgPickerStep: React.FC<{
  orgs: { id: string; name: string }[];
  defaultOrgId: string | null;
  onContinue: (orgId: string) => void;
}> = ({ orgs, defaultOrgId, onContinue }) => {
  const [sel, setSel] = useState(defaultOrgId ?? orgs[0]?.id ?? '');
  return (
    <div className="px-5 py-12 max-w-md mx-auto text-center">
      <p className="text-[13.5px] text-foreground font-semibold mb-1">
        Which business is this definition for?
      </p>
      <p className="text-[12px] text-muted-foreground mb-4">
        The AI drafts it using that business&apos;s connected data sources.
      </p>
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13px] text-foreground mb-3 focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!sel}
        onClick={() => onContinue(sel)}
        className="w-full rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
};

export default SemanticsPage;
